import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Radio, Plus, Users, Copy, Calendar, Ticket, ShoppingCart, X, Search, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * AdminLiveEvents — ticketed live-stream events (Jitsi room per event).
 * Host joins as moderator automatically (first-in-room on Jitsi public
 * server). Ticket sales flow through a PayPal.me link for the MVP.
 */
export default function AdminLiveEvents() {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', starts_at: '', duration_min: 60, price: 0, max_tickets: 100 });
  const [merchEvent, setMerchEvent] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get('/live-events').then(r => setEvents(r.data)).catch(() => setEvents([]));
  useEffect(() => { load(); }, []);

  const createEvent = async () => {
    if (!form.title.trim() || !form.starts_at) { toast.error('Title and start time required'); return; }
    try {
      await api.post('/admin/live-events', { ...form, starts_at: new Date(form.starts_at).toISOString() });
      setOpen(false); setForm({ title:'', description:'', starts_at:'', duration_min:60, price:0, max_tickets:100 }); load();
      toast.success('Event created');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const copyTicket = async (ev) => {
    const url = `${window.location.origin}/event/${ev.slug}`;
    try { await navigator.clipboard.writeText(url); toast.success('Ticket page link copied'); }
    catch { toast.error('Clipboard blocked'); }
  };

  const startLive = async (ev) => {
    const { data } = await api.post(`/admin/live-events/${ev.id}/host-token`);
    // Host joins a dedicated URL that uses the event slug + display name.
    navigate(`/call/host-event-${ev.id}?slug=${encodeURIComponent(data.slug)}&name=${encodeURIComponent(data.display_name)}`);
  };

  return (
    <div className='p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto' data-testid='admin-live-events-page'>
      <div className='flex items-start justify-between mb-5 flex-wrap gap-3'>
        <div>
          <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Live</div>
          <h1 className='font-display text-3xl font-bold text-slate-900 dark:text-slate-50 mt-1'>Live Events &amp; Webinars</h1>
          <p className='text-slate-500 dark:text-slate-400 text-sm mt-1'>Sell tickets, cap attendance, and go live. Jitsi handles ~75 attendees free per room.</p>
        </div>
        <Button onClick={() => setOpen(true)} className='bg-[#C8102E] hover:bg-[#A50D26] text-white font-semibold' data-testid='add-event-btn'>
          <Plus className='w-4 h-4 mr-2' /> New event
        </Button>
      </div>

      {events.length === 0 ? (
        <Card className='p-10 text-center text-slate-500 dark:text-slate-400'>
          <Radio className='w-10 h-10 mx-auto mb-2 text-slate-300 dark:text-slate-600' /> No events yet.
        </Card>
      ) : (
        <div className='grid md:grid-cols-2 gap-4'>
          {events.map(ev => (
            <Card key={ev.id} className='p-5 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800' data-testid={`event-card-${ev.id}`}>
              <div className='flex items-start justify-between gap-2 mb-2'>
                <div>
                  <h3 className='font-display font-semibold text-slate-900 dark:text-slate-50'>{ev.title}</h3>
                  <div className='text-xs text-slate-500 dark:text-slate-400 mt-0.5'>
                    <Calendar className='w-3 h-3 inline mr-1' /> {new Date(ev.starts_at).toLocaleString()}
                  </div>
                </div>
                <Badge className='bg-[#C8102E] text-white'>{ev.price > 0 ? `$${ev.price}` : 'Free'}</Badge>
              </div>
              <div className='text-xs text-slate-500 dark:text-slate-400 mb-3 flex items-center gap-3'>
                <span><Ticket className='w-3 h-3 inline mr-1' /> {ev.tickets_sold}/{ev.max_tickets} tickets</span>
                <span><Users className='w-3 h-3 inline mr-1' /> {ev.duration_min} min</span>
              </div>
              <div className='flex gap-2 flex-wrap'>
                <Button size='sm' onClick={() => startLive(ev)} className='bg-[#C8102E] text-white' data-testid={`event-start-${ev.id}`}>Go live</Button>
                <Button size='sm' variant='outline' onClick={() => copyTicket(ev)} data-testid={`event-copy-${ev.id}`}><Copy className='w-3.5 h-3.5 mr-1' /> Ticket link</Button>
                <Button size='sm' variant='outline' onClick={() => setMerchEvent(ev)} data-testid={`event-merch-${ev.id}`}>
                  <ShoppingCart className='w-3.5 h-3.5 mr-1' /> Merch
                  {(ev.product_ids?.length || 0) > 0 && (
                    <span className='ml-1.5 text-[10px] bg-[#1B3A87] text-white rounded-full px-1.5'>{ev.product_ids.length}</span>
                  )}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className='max-w-md' data-testid='add-event-dialog'>
          <DialogHeader><DialogTitle>New live event</DialogTitle></DialogHeader>
          <div className='space-y-3'>
            <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder='Masterclass: Reactive Dog' data-testid='nev-title' /></div>
            <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid='nev-desc' /></div>
            <div className='grid grid-cols-2 gap-3'>
              <div><Label>Starts *</Label><Input type='datetime-local' value={form.starts_at} onChange={e => setForm({ ...form, starts_at: e.target.value })} data-testid='nev-start' /></div>
              <div><Label>Duration (min)</Label><Input type='number' value={form.duration_min} onChange={e => setForm({ ...form, duration_min: Number(e.target.value || 60) })} /></div>
              <div><Label>Price ($)</Label><Input type='number' min='0' value={form.price} onChange={e => setForm({ ...form, price: Number(e.target.value || 0) })} data-testid='nev-price' /></div>
              <div><Label>Max tickets</Label><Input type='number' min='1' value={form.max_tickets} onChange={e => setForm({ ...form, max_tickets: Number(e.target.value || 100) })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={createEvent} className='bg-[#C8102E] text-white' data-testid='nev-submit'>Create event</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merch picker — pin up to 6 POS products to the live-stream sidebar */}
      <Dialog open={!!merchEvent} onOpenChange={(v) => !v && setMerchEvent(null)}>
        <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto' data-testid='merch-picker-dialog'>
          {merchEvent && (
            <MerchPicker
              event={merchEvent}
              onSaved={() => { setMerchEvent(null); load(); }}
              onClose={() => setMerchEvent(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- MerchPicker: cap=6, drag-free select UI ---------- */
const MAX_UPSELLS = 6;
const MerchPicker = ({ event, onSaved, onClose }) => {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState([]); // ordered product ids
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/pos/products'),
      api.get(`/admin/live-events/${event.id}`).catch(() => ({ data: event })),
    ]).then(([pr, er]) => {
      setProducts(pr.data || []);
      setSelected(er.data?.product_ids || event.product_ids || []);
    });
  }, [event]);

  const productsById = products.reduce((acc, p) => { acc[p.id] = p; return acc; }, {});
  const filtered = products.filter(p => {
    if (!q.trim()) return true;
    const t = q.trim().toLowerCase();
    return p.name.toLowerCase().includes(t) || (p.category || '').toLowerCase().includes(t);
  });

  const toggle = (id) => {
    if (selected.includes(id)) {
      setSelected(selected.filter(x => x !== id));
    } else {
      if (selected.length >= MAX_UPSELLS) {
        toast.error(`Up to ${MAX_UPSELLS} products per event`);
        return;
      }
      setSelected([...selected, id]);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/live-events/${event.id}`, { product_ids: selected });
      toast.success('Merch sidebar saved');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const selectedProducts = selected.map(id => productsById[id]).filter(Boolean);

  return (
    <div>
      <div className='mb-4'>
        <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Live-stream merch</div>
        <h2 className='font-display text-xl font-bold mt-1'>{event.title}</h2>
        <p className='text-xs text-slate-500 mt-1'>Pin up to {MAX_UPSELLS} POS products. Viewers see them in a sidebar during the stream.</p>
      </div>

      {/* Preview sidebar — matches PublicEvent viewer */}
      <div className='mb-4'>
        <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1'>Sidebar preview ({selectedProducts.length}/{MAX_UPSELLS})</div>
        <div className='bg-gradient-to-b from-[#0B1220] to-[#1B3A87] rounded-lg p-3 text-white' data-testid='merch-preview'>
          <div className='text-sm font-display font-bold mb-2 flex items-center gap-2'>
            <ShoppingCart className='w-4 h-4' /> Shop the stream
          </div>
          {selectedProducts.length === 0 ? (
            <div className='text-xs text-slate-300'>Pick products below to feature them here.</div>
          ) : (
            <div className='space-y-2'>
              {selectedProducts.map(p => (
                <div key={p.id} className='flex items-center gap-3 p-2 rounded hover:bg-white/10'>
                  {(p.image_base64 || p.image_url) ? (
                    <img src={p.image_base64 || p.image_url} alt='' className='w-10 h-10 rounded object-cover bg-white/5' />
                  ) : (
                    <div className='w-10 h-10 rounded bg-white/5 flex items-center justify-center text-xs text-slate-300'>IMG</div>
                  )}
                  <div className='flex-1 min-w-0'>
                    <div className='font-semibold truncate text-sm'>{p.name}</div>
                    <div className='text-xs text-slate-300'>${(p.price || 0).toFixed(2)}</div>
                  </div>
                  <button onClick={() => toggle(p.id)} className='text-slate-300 hover:text-white' title='Remove' data-testid={`merch-preview-remove-${p.id}`}>
                    <X className='w-4 h-4' />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Picker */}
      <div className='mb-3'>
        <div className='relative'>
          <Search className='w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400' />
          <Input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder='Search POS catalog…'
            className='pl-7 h-9'
            data-testid='merch-search'
          />
        </div>
      </div>
      <div className='border border-slate-200 dark:border-slate-700 rounded-lg max-h-72 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800'>
        {products.length === 0 ? (
          <div className='px-3 py-6 text-xs text-slate-500 text-center'>No POS products yet. Create products first in the POS screen.</div>
        ) : filtered.length === 0 ? (
          <div className='px-3 py-4 text-xs text-slate-500 text-center'>No matches.</div>
        ) : filtered.map(p => {
          const isOn = selected.includes(p.id);
          const atCap = !isOn && selected.length >= MAX_UPSELLS;
          return (
            <button
              key={p.id}
              type='button'
              onClick={() => toggle(p.id)}
              disabled={atCap}
              className={`w-full text-left px-3 py-2 flex items-center gap-3 text-sm transition-colors ${
                isOn ? 'bg-[#167A5A]/10' : atCap ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-50 dark:hover:bg-slate-800'
              }`}
              data-testid={`merch-option-${p.id}`}
            >
              <div className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${
                isOn ? 'bg-[#167A5A] border-[#167A5A] text-white' : 'border-slate-300 dark:border-slate-600'
              }`}>
                {isOn && <Check className='w-3.5 h-3.5' />}
              </div>
              {(p.image_base64 || p.image_url) ? (
                <img src={p.image_base64 || p.image_url} alt='' className='w-10 h-10 rounded object-cover' />
              ) : (
                <div className='w-10 h-10 rounded bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-[10px] text-slate-400'>No img</div>
              )}
              <div className='flex-1 min-w-0'>
                <div className='font-semibold truncate text-slate-800 dark:text-slate-100'>{p.name}</div>
                <div className='text-[11px] text-slate-500 dark:text-slate-400 truncate'>{p.category || 'Uncategorised'}</div>
              </div>
              <div className='font-bold text-slate-700 dark:text-slate-200 text-sm'>${(p.price || 0).toFixed(2)}</div>
            </button>
          );
        })}
      </div>

      <DialogFooter className='mt-4'>
        <Button variant='outline' onClick={onClose}>Cancel</Button>
        <Button
          onClick={save}
          disabled={saving}
          className='bg-[#C8102E] hover:bg-[#A50D26] text-white'
          data-testid='merch-save-btn'
        >
          Save merch sidebar
        </Button>
      </DialogFooter>
    </div>
  );
};
